import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import React, {useCallback, useEffect, useRef, useState} from "react";
import styles from "./styles.module.scss";
import closeIcon from "../../../../public/images/close-icon.svg";
import ScheduleItem from "./ScheduleItem";
import ButtonsContainer from "./ButtonsContainer";
import UserAvatar from "app/components/UserAvatar";
import TabButton from "app/components/Button/TabButton";
import {useTranslation} from "react-i18next";
import {schedulingFetchGet} from "app/utils/request/scheduling/fetch";
import {Loading} from "app/components/Wrappers";
import CalendarIco from "../../../../public/images/calendar-ico-gray.svg";
import HistoryIco from "../../../../public/images/history-ico.svg";
import {formatDateToShortString, formatTimeFromISO, getDurationInMinutes, groupByMonthYear} from "./utils";

interface AthleteDataProps {
  athleteId: string;
  title: string;
  avatar: string;
  email: string;
  phone: string;
}

interface Props {
  isModalOpen: boolean;
  modalHandler: () => void;
  athleteData: AthleteDataProps;
}

const ScheduleModal = ({ isModalOpen, modalHandler, athleteData }: Props) => {
  const { t } = useTranslation();
  const listContainerRef = useRef(null);
  const loaderRef = useRef(null);

  const schedule_options = [
    { value: "upcoming", label: t("Upcoming")},
    { value: "history", label: t("History")},
  ];

  const [scheduleType, setScheduleType] = useState(schedule_options[0]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreUpcoming, setHasMoreUpcoming] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  const isUpcoming = scheduleType.value === "upcoming";
  const currentEvents = isUpcoming ? upcomingEvents : historyEvents;
  const hasMoreEvents = isUpcoming ? hasMoreUpcoming : hasMoreHistory;

  const fetchEvents = useCallback(async () => {
    if (loading || !isModalOpen) { return; }

    const isUpcoming = scheduleType.value === "upcoming";
    const hasMore = isUpcoming ? hasMoreUpcoming : hasMoreHistory;
    if (!hasMore) { return; }

    const page = isUpcoming ? upcomingPage : historyPage;
    const endpoint = isUpcoming ? 'upcoming_events' : 'history_events';
    const setEvents = isUpcoming ? setUpcomingEvents : setHistoryEvents;
    const setPage = isUpcoming ? setUpcomingPage : setHistoryPage;
    const setHasMore = isUpcoming ? setHasMoreUpcoming : setHasMoreHistory;
    setLoading(true);
    try {
      const response = await schedulingFetchGet(`student_profiles/${athleteData.athleteId}/${endpoint}`, {page});
      const formatted = response.events.map((event: any, index) => ({
        id: `${event.id || index}-${page}`,
        date: formatDateToShortString(event.start),
        name: `${event.coachProfile.firstName} ${event.coachProfile.lastName}`,
        profile_image: event.coachProfile.profilePicture,
        title: event.lessonRateTitle,
        time: formatTimeFromISO(event.start),
        duration: getDurationInMinutes(event.start, event.end),
      }));

      setEvents(prev => [...prev, ...formatted]);
      setPage(prev => prev + 1);
      setHasMore(response.nextPage !== null);
    } catch (error) {
      console.error("Error fetching events:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [athleteData.athleteId, scheduleType.value, loading, hasMoreUpcoming, hasMoreHistory, upcomingPage, historyPage]);


  useEffect(() => {
    if (!isModalOpen) { return; }

    const isUpcoming = scheduleType.value === "upcoming";
    const events = isUpcoming ? upcomingEvents : historyEvents;

    if (events.length === 0) {
      fetchEvents();
    }
  }, [isModalOpen, scheduleType, fetchEvents, upcomingEvents, historyEvents]);

  useEffect(() => {
    if (!isModalOpen) {
      setUpcomingEvents([]);
      setHistoryEvents([]);
      setUpcomingPage(1);
      setHistoryPage(1);
      setHasMoreUpcoming(true);
      setHasMoreHistory(true);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          if (hasMoreEvents) {
            fetchEvents();
          }
        }
      },
      { threshold: 0.1, rootMargin: '10px' }
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [fetchEvents, hasMoreEvents, loading]);

  const renderEventGroups = () => {
    if (loading && currentEvents.length === 0) {
      return <Loading isLoading loadType="spinner" />;
    }

    if (currentEvents.length === 0 && !loading) {
      if (isUpcoming) {
        return (
          <div className={styles.no_events_container}>
            <img src={CalendarIco} alt="calendar" className={styles.no_events_icon} />
            <p className={styles.no_events_title}>{t("No upcoming events")}</p>
            <p className={styles.no_events_subtitle}>{t("All upcoming sessions will be displayed here")}</p>
          </div>
        );
      } else {
        return (
          <div className={styles.no_events_container}>
            <img src={HistoryIco} alt="calendar" className={styles.no_events_icon} />
            <p className={styles.no_events_title}>{t("No history yet")}</p>
            <p className={styles.no_events_subtitle}>{t("All committed sessions will be displayed here")}</p>
          </div>
        );
      }
    }

    return groupByMonthYear(currentEvents).map((item: any) => (
      <div key={item.date} className={styles.month_group}>
        <p className={styles.title_text}>{item.date}</p>
        {item.values?.map((value) => (
          <ScheduleItem key={value.id} schedule={value} />
        ))}
      </div>
    ));
  };

  const tabsList = [
    // {
    //   label: t("Spaces"),
    //   isActive: false,
    //   onClick: () => null,
    // },
    {
      label: t("Bookings"),
      isActive: true,
      onClick: () => null,
    },
    // {
    //   label: t("Purchases"),
    //   isActive: false,
    //   onClick: () => null,
    // }
  ];

  return (
    <ModalLayout isOpen={isModalOpen} onClose={modalHandler}>
      <div className={styles.content_wrapper}>
        <div className={styles.modal_header}>
          <div className={styles.header_title_container}>
            {athleteData.avatar && (
              <UserAvatar
                sizeHeight={64}
                sizeWidth={64}
                altName="avatar"
                className={styles.header_logo}
                srcName={athleteData.avatar}
                userName={athleteData.title}
              />
            )}
            <div>
              <h1 className={styles.title}>{athleteData.title}</h1>
              <div className={styles.sub_title}>
                {athleteData.email && <p>{athleteData.email}</p>}
                {athleteData.phone && <p>{athleteData.phone}</p>}
              </div>
            </div>
          </div>
          <img
            src={closeIcon}
            alt="close"
            className={styles.close_icon}
            onClick={modalHandler}
          />
        </div>
        <div className={styles.navbar_wrapper}>
          {tabsList.map((tab) => (
            <TabButton
              label={tab.label}
              isActive={tab.isActive}
              onClick={tab.onClick}
              key={tab.label} />
          ))}
        </div>

        <ButtonsContainer
          items={schedule_options}
          containerStyle={styles.option_button_group}
          itemSelected={scheduleType}
          setItemSelected={setScheduleType}
        />

        <div className={styles.list_container} ref={listContainerRef}>
          {renderEventGroups()}

          {loading && currentEvents.length > 0 && (
            <div className={styles.loading_more}>
              <Loading isLoading loadType="spinner" />
            </div>
          )}

          {hasMoreEvents && (
            <div ref={loaderRef} className={styles.loader_observer_spacer} />
          )}
        </div>
      </div>
    </ModalLayout>
  );
};

export default ScheduleModal;
